import React, { useState, useRef, useEffect } from 'react';
import {
    View,
    TextInput,
    Animated,
    Text,
    StyleSheet,
    TextInputProps,
    ViewStyle,
    TextStyle,
} from 'react-native';
import { theme } from '../constants/theme';

interface FloatingLabelInputProps extends TextInputProps {
    label: string;
    error?: string;
    containerStyle?: ViewStyle;
    labelStyle?: TextStyle;
    inputStyle?: TextStyle;
    rightIcon?: React.ReactNode;
}

export const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
    label,
    value,
    error,
    containerStyle,
    labelStyle,
    inputStyle,
    rightIcon,
    onFocus,
    onBlur,
    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const animatedIsFocused = useRef(new Animated.Value(value ? 1 : 0)).current;

    const handleFocus = (e: any) => {
        setIsFocused(true);
        onFocus?.(e);
    };

    const handleBlur = (e: any) => {
        setIsFocused(false);
        onBlur?.(e);
    };

    useEffect(() => {
        Animated.timing(animatedIsFocused, {
            toValue: isFocused || value ? 1 : 0,
            duration: 200,
            useNativeDriver: false,
        }).start();
    }, [isFocused, value]);

    const labelStyleAnimated = {
        position: 'absolute' as const,
        left: 0,
        top: animatedIsFocused.interpolate({
            inputRange: [0, 1],
            outputRange: [18, 0], // Adjust these values based on padding
        }),
        fontSize: animatedIsFocused.interpolate({
            inputRange: [0, 1],
            outputRange: [16, 12],
        }),
        color: animatedIsFocused.interpolate({
            inputRange: [0, 1],
            outputRange: [theme.colors.textSecondary, theme.colors.primary],
        }),
    };

    return (
        <View style={[styles.container, containerStyle]}>
            <Animated.Text style={[labelStyleAnimated, labelStyle]}>
                {label}
            </Animated.Text>
            <View style={[styles.inputContainer, error && styles.inputContainerError]}>
                <TextInput
                    style={[styles.input, inputStyle]}
                    value={value}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    blurOnSubmit
                    placeholderTextColor="transparent" // Hide placeholder as we use label
                    {...props}
                />
                {rightIcon && <View style={styles.rightIcon}>{rightIcon}</View>}
            </View>
            {error && <Text style={styles.error}>{error}</Text>}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        marginBottom: theme.spacing.lg,
        paddingTop: 18, // Space for the floating label
    },
    inputContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.textSecondary, // Inactive color
        paddingVertical: 4, // Reduced padding
    },
    inputContainerError: {
        borderBottomColor: theme.colors.error,
    },
    input: {
        flex: 1,
        fontSize: 16,
        color: theme.colors.text,
        paddingVertical: 4,
        paddingHorizontal: 0, // Remove default padding
    },
    rightIcon: {
        marginLeft: theme.spacing.sm,
    },
    error: {
        ...theme.typography.caption,
        color: theme.colors.error,
        marginTop: 4,
    },
});
